// A single bar, sticky under the topbar, shown above the review list whenever
// conflicts exist, letting the user apply one of the three per-conflict
// resolutions to every currently unresolved conflict at once. Per-row
// resolution (BulkConflictDialog) stays available for one-off overrides — this
// is an addition, not a replacement. It is purely an indicator/action toolbar,
// not itself a conflict — its count always mirrors the review's own
// `detectConflicts` result, never a separate tally.
//
// Once applied, the bar turns green and names the resolution instead of
// disappearing, with an "Annuler" to revert to the pre-resolution snapshot
// (see BulkReview, which owns that snapshot and clears it once a new conflict
// appears again).
import type { GlobalResolutionKind } from '@/lib/bulk-add/bulk-session';

const KIND_LABELS: Record<GlobalResolutionKind, string> = {
  merge: 'Fusion de tous les conflits',
  keepBoth: 'Conservation de tous les mots',
  deleteExtra: 'Suppression des doublons',
};

interface BulkConflictBarProps {
  count: number;
  onApply: (kind: GlobalResolutionKind) => void;
  /** Set once the bar's own "apply to all" action has resolved every conflict. */
  resolvedKind?: GlobalResolutionKind | null;
  onUndo?: () => void;
}

/** "Apply to all" bar for resolving every unresolved conflict at once. */
export function BulkConflictBar({ count, onApply, resolvedKind, onUndo }: BulkConflictBarProps) {
  if (resolvedKind) {
    return (
      <div className="bulk-conflict-bar bulk-conflict-bar--resolved">
        <span className="bulk-conflict-bar__label">
          <span className="material-symbols-rounded" aria-hidden="true">
            check_circle
          </span>
          Conflits résolus — {KIND_LABELS[resolvedKind]}
        </span>
        <div className="bulk-conflict-bar__actions">
          <button type="button" className="bulk-conflict-bar__btn" onClick={onUndo}>
            <span className="material-symbols-rounded" aria-hidden="true">
              undo
            </span>
            Annuler la résolution
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-conflict-bar">
      <span className="bulk-conflict-bar__label">
        <span className="material-symbols-rounded" aria-hidden="true">
          error
        </span>
        {count} conflit{count > 1 ? 's' : ''} à résoudre — tout résoudre en une fois&nbsp;:
      </span>
      <div className="bulk-conflict-bar__actions">
        <button type="button" className="bulk-conflict-bar__btn" onClick={() => onApply('merge')}>
          Tout fusionner
        </button>
        <button
          type="button"
          className="bulk-conflict-bar__btn"
          onClick={() => onApply('keepBoth')}
        >
          Tout garder
        </button>
        <button
          type="button"
          className="bulk-conflict-bar__btn"
          onClick={() => onApply('deleteExtra')}
        >
          Tout dédupliquer
        </button>
      </div>
    </div>
  );
}
